import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  collaboratorsApiService, 
  Collaborator, 
  CollaboratorCreateDTO, 
  CollaboratorUpdateDTO, 
  CollaboratorFilters 
} from '../api/collaborators-api';

// Estado inicial
interface CollaboratorsState {
  collaborators: Collaborator[];
  currentCollaborator: Collaborator | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: CollaboratorFilters;
  stats: {
    total: number;
    active: number;
    inactive: number;
    pending: number;
    onLeave: number;
  } | null;
}

const initialState: CollaboratorsState = {
  collaborators: [],
  currentCollaborator: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  stats: null,
};

// Async Thunks
export const fetchCollaborators = createAsyncThunk(
  'collaborators/fetchCollaborators',
  async (filters: CollaboratorFilters = {}, { rejectWithValue }) => {
    try {
      const response = await collaboratorsApiService.getCollaborators(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar colaboradores'
      );
    }
  }
);

export const fetchCollaborator = createAsyncThunk(
  'collaborators/fetchCollaborator',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await collaboratorsApiService.getCollaboratorById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar colaborador'
      );
    }
  }
);

export const createCollaborator = createAsyncThunk(
  'collaborators/createCollaborator',
  async (data: CollaboratorCreateDTO, { rejectWithValue }) => {
    try {
      const response = await collaboratorsApiService.createCollaborator(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear colaborador'
      );
    }
  }
);

export const updateCollaborator = createAsyncThunk(
  'collaborators/updateCollaborator',
  async (
    { id, data }: { id: string; data: CollaboratorUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await collaboratorsApiService.updateCollaborator(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar colaborador'
      );
    }
  }
);

export const deleteCollaborator = createAsyncThunk(
  'collaborators/deleteCollaborator',
  async (id: string, { rejectWithValue }) => {
    try {
      await collaboratorsApiService.deleteCollaborator(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar colaborador'
      );
    }
  }
);

export const toggleCollaboratorStatus = createAsyncThunk(
  'collaborators/toggleCollaboratorStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await collaboratorsApiService.toggleCollaboratorStatus(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado del colaborador'
      );
    }
  }
);

export const fetchCollaboratorStats = createAsyncThunk(
  'collaborators/fetchCollaboratorStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await collaboratorsApiService.getCollaboratorStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

// Slice
const collaboratorsSlice = createSlice({
  name: 'collaborators',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<CollaboratorFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentCollaborator: (state, action: PayloadAction<Collaborator | null>) => {
      state.currentCollaborator = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: builder => {
    // Fetch collaborators
    builder
      .addCase(fetchCollaborators.pending, state => {
        if (state.collaborators.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchCollaborators.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        
        // El payload viene directamente del API service que ya extrajo data
        // Formato esperado: { collaborators: [], total, page, limit, totalPages }
        state.collaborators = Array.isArray(payload.collaborators) ? payload.collaborators : [];
        
        const newPage = payload.page || 1;
        const newLimit = payload.limit || 10;
        const newTotal = payload.total || 0;
        const newTotalPages = payload.totalPages || 1;
        
        if (
          state.pagination.page !== newPage ||
          state.pagination.limit !== newLimit ||
          state.pagination.total !== newTotal ||
          state.pagination.totalPages !== newTotalPages
        ) {
          state.pagination = {
            page: newPage,
            limit: newLimit,
            total: newTotal,
            totalPages: newTotalPages,
          };
        }
      })
      .addCase(fetchCollaborators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch collaborator
    builder
      .addCase(fetchCollaborator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollaborator.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCollaborator = action.payload as Collaborator;
      })
      .addCase(fetchCollaborator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create collaborator
    builder
      .addCase(createCollaborator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCollaborator.fulfilled, (state, action) => {
        state.loading = false;
        const newCollaborator = action.payload as Collaborator;
        state.collaborators.unshift(newCollaborator);
        state.pagination.total += 1;
        
        // Actualizar estadísticas si están cargadas
        if (state.stats) {
          state.stats.total += 1;
          if (newCollaborator.status === 'active') {
            state.stats.active += 1;
          } else if (newCollaborator.status === 'inactive') {
            state.stats.inactive += 1;
          } else if (newCollaborator.status === 'pending') {
            state.stats.pending += 1;
          } else if (newCollaborator.status === 'on_leave') {
            state.stats.onLeave += 1;
          }
        }
      })
      .addCase(createCollaborator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update collaborator
    builder
      .addCase(updateCollaborator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCollaborator.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCollaborator = action.payload as Collaborator;
        const index = state.collaborators.findIndex(
          collaborator => collaborator.id === updatedCollaborator.id
        );
        if (index !== -1) {
          const oldStatus = state.collaborators[index].status;
          state.collaborators[index] = updatedCollaborator;
          
          // Actualizar estadísticas si cambió el estado
          if (state.stats && oldStatus !== updatedCollaborator.status) {
            // Decrementar el estado anterior
            if (oldStatus === 'active') state.stats.active -= 1;
            else if (oldStatus === 'inactive') state.stats.inactive -= 1;
            else if (oldStatus === 'pending') state.stats.pending -= 1;
            else if (oldStatus === 'on_leave') state.stats.onLeave -= 1;
            
            // Incrementar el nuevo estado
            if (updatedCollaborator.status === 'active') state.stats.active += 1;
            else if (updatedCollaborator.status === 'inactive') state.stats.inactive += 1;
            else if (updatedCollaborator.status === 'pending') state.stats.pending += 1;
            else if (updatedCollaborator.status === 'on_leave') state.stats.onLeave += 1;
          }
        }
        if (state.currentCollaborator?.id === updatedCollaborator.id) {
          state.currentCollaborator = updatedCollaborator;
        }
      })
      .addCase(updateCollaborator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete collaborator
    builder
      .addCase(deleteCollaborator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCollaborator.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        const deletedCollaborator = state.collaborators.find(c => c.id === deletedId);
        state.collaborators = state.collaborators.filter(
          collaborator => collaborator.id !== deletedId
        );
        state.pagination.total -= 1;
        
        // Actualizar estadísticas si están cargadas
        if (state.stats && deletedCollaborator) {
          state.stats.total -= 1;
          if (deletedCollaborator.status === 'active') state.stats.active -= 1;
          else if (deletedCollaborator.status === 'inactive') state.stats.inactive -= 1;
          else if (deletedCollaborator.status === 'pending') state.stats.pending -= 1;
          else if (deletedCollaborator.status === 'on_leave') state.stats.onLeave -= 1;
        }
        
        if (state.currentCollaborator?.id === deletedId) {
          state.currentCollaborator = null;
        }
      })
      .addCase(deleteCollaborator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Toggle collaborator status
    builder
      .addCase(toggleCollaboratorStatus.pending, state => {
        state.error = null;
      })
      .addCase(toggleCollaboratorStatus.fulfilled, (state, action) => {
        // Extraer el colaborador si viene en un wrapper (como en plans)
        const collaboratorData = (action.payload as any)?.data || action.payload;
        const updatedCollaborator = collaboratorData as Collaborator;
        
        // Asegurar que state.collaborators sea un array
        if (!Array.isArray(state.collaborators)) {
          state.collaborators = [];
        }
        
        const index = state.collaborators.findIndex(
          collaborator => collaborator.id === updatedCollaborator.id
        );
        if (index !== -1) {
          const oldStatus = state.collaborators[index].status;
          state.collaborators[index] = updatedCollaborator;
          
          // Actualizar estadísticas si están cargadas
          if (state.stats && oldStatus !== updatedCollaborator.status) {
            if (oldStatus === 'active') {
              state.stats.active = Math.max(0, (state.stats.active || 0) - 1);
              state.stats.inactive = (state.stats.inactive || 0) + 1;
            } else {
              state.stats.active = (state.stats.active || 0) + 1;
              state.stats.inactive = Math.max(0, (state.stats.inactive || 0) - 1);
            }
          }
        }
        if (state.currentCollaborator?.id === updatedCollaborator.id) {
          state.currentCollaborator = updatedCollaborator;
        }
      })
      .addCase(toggleCollaboratorStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch collaborator stats
    builder
      .addCase(fetchCollaboratorStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollaboratorStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload as any;
      })
      .addCase(fetchCollaboratorStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentCollaborator,
  clearError,
  setPagination,
} = collaboratorsSlice.actions;

// Selectors
export const selectCollaborators = (state: { collaborators: CollaboratorsState }) => state.collaborators.collaborators;
export const selectCurrentCollaborator = (state: { collaborators: CollaboratorsState }) => state.collaborators.currentCollaborator;
export const selectCollaboratorsLoading = (state: { collaborators: CollaboratorsState }) => state.collaborators.loading;
export const selectCollaboratorsError = (state: { collaborators: CollaboratorsState }) => state.collaborators.error;
export const selectCollaboratorsPagination = (state: { collaborators: CollaboratorsState }) => state.collaborators.pagination;
export const selectCollaboratorsFilters = (state: { collaborators: CollaboratorsState }) => state.collaborators.filters;
export const selectCollaboratorsStats = (state: { collaborators: CollaboratorsState }) => state.collaborators.stats;

export default collaboratorsSlice.reducer;

